import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

const TODAY_ACTIVITY = [
  { rep:'Habeeb H.', calls:8, emailsSent:24, tasksCompleted:6, dealsAdvanced:3, meetingsHeld:2, notesLogged:5 },
  { rep:'Amira S.',  calls:3, emailsSent:18, tasksCompleted:4, dealsAdvanced:1, meetingsHeld:1, notesLogged:3 },
  { rep:'David O.',  calls:2, emailsSent:9,  tasksCompleted:7, dealsAdvanced:0, meetingsHeld:0, notesLogged:2 },
  { rep:'Kweku M.',  calls:1, emailsSent:6,  tasksCompleted:3, dealsAdvanced:0, meetingsHeld:1, notesLogged:1 },
];

const WEEKLY_TREND = [
  { day:'Mon', calls:12, emails:45, tasks:18 },
  { day:'Tue', calls:9,  emails:38, tasks:22 },
  { day:'Wed', calls:14, emails:52, tasks:15 },
  { day:'Thu', calls:11, emails:41, tasks:20 },
  { day:'Fri', calls:8,  emails:29, tasks:14 },
];

const RECENT_ACTIVITY = [
  { rep:'Habeeb H.', action:'Logged call with James Morris — 12m 34s', time:'10 min ago', type:'call'  },
  { rep:'Amira S.',  action:'Sent email campaign: Scout Activation Wave 2', time:'24 min ago', type:'email' },
  { rep:'Habeeb H.', action:'Closed ticket TK-003 — Payment issue resolved', time:'1h ago',    type:'task'  },
  { rep:'David O.',  action:'Added note to Arsenal FC deal', time:'1h ago',    type:'note'  },
  { rep:'Kweku M.',  action:'Completed task: Approve Kwame Asante trial', time:'2h ago',    type:'task'  },
  { rep:'Habeeb H.', action:'Booked meeting with Emma Thompson — Apr 8', time:'3h ago',    type:'meet'  },
  { rep:'Amira S.',  action:'Created quote QT-003 for Juventus', time:'4h ago',    type:'deal'  },
];

const typeColor = t => ({ call:'#4fc3f7', email:'#A7F432', task:'#f5a623', note:'#a78bfa', meet:'#8a9a97', deal:'#A7F432' })[t]||'#8a9a97';

const Tip = ({ active,payload,label }) => active&&payload?.length?(
  <div style={{ background:'#131B1B',border:'1px solid #2a3a3a',borderRadius:8,padding:'8px 12px',fontSize:12 }}>
    <p style={{ color:'var(--text-muted)',marginBottom:4 }}>{label}</p>
    {payload.map((p,i)=><p key={i} style={{ color:p.color||'var(--accent)' }}>{p.name}: {p.value}</p>)}
  </div>
):null;

export default function ActivityDashboard() {
  const [period, setPeriod] = useState('Today');

  const totals = TODAY_ACTIVITY.reduce((acc,r)=>({
    calls:acc.calls+r.calls, emails:acc.emailsSent+r.emailsSent,
    tasks:acc.tasks+r.tasksCompleted, deals:acc.deals+r.dealsAdvanced,
  }),{ calls:0,emails:0,tasks:0,deals:0 });

  return (
    <div>
      <div className="stats-grid">
        <div className="stat-card"><div className="stat-label">Calls made</div><div className="stat-value">{totals.calls}</div><div className="stat-sub stat-up">Today</div></div>
        <div className="stat-card"><div className="stat-label">Emails sent</div><div className="stat-value">{totals.emails}</div><div className="stat-sub stat-up">Today</div></div>
        <div className="stat-card"><div className="stat-label">Tasks completed</div><div className="stat-value">{totals.tasks}</div><div className="stat-sub stat-up">Today</div></div>
        <div className="stat-card"><div className="stat-label">Deals advanced</div><div className="stat-value">{totals.deals}</div><div className="stat-sub stat-up">Today</div></div>
      </div>

      <div style={{ display:'flex', gap:6, marginBottom:20 }}>
        {['Today','This week','This month'].map(p=><button key={p} className={`pill ${period===p?'active':''}`} onClick={()=>setPeriod(p)}>{p}</button>)}
      </div>

      <div className="two-col" style={{ marginBottom:20 }}>
        <div className="card">
          <div className="card-header"><span className="card-title">Weekly activity trend</span></div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={WEEKLY_TREND} margin={{ top:4,right:4,left:-20,bottom:0 }}>
              <XAxis dataKey="day" tick={{ fill:'#646B6B',fontSize:11 }} axisLine={false} tickLine={false}/>
              <YAxis tick={{ fill:'#646B6B',fontSize:11 }} axisLine={false} tickLine={false}/>
              <Tooltip content={<Tip />}/>
              <Bar dataKey="calls"  name="Calls"  fill="#4fc3f7" radius={[4,4,0,0]}/>
              <Bar dataKey="emails" name="Emails" fill="#A7F432" radius={[4,4,0,0]}/>
              <Bar dataKey="tasks"  name="Tasks"  fill="#f5a623" radius={[4,4,0,0]}/>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="card">
          <div className="card-header"><span className="card-title">Live activity feed</span><span className="live-indicator"><span className="live-dot"/> Live</span></div>
          <div style={{ display:'flex',flexDirection:'column',gap:10 }}>
            {RECENT_ACTIVITY.map((a,i)=>(
              <div key={i} style={{ display:'flex',gap:10,alignItems:'flex-start' }}>
                <div style={{ width:8,height:8,borderRadius:'50%',background:typeColor(a.type),marginTop:5,flexShrink:0 }}/>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:12,color:'var(--text-primary)' }}><strong style={{ color:'var(--accent)' }}>{a.rep}</strong> {a.action}</div>
                  <div style={{ fontSize:11,color:'var(--text-dim)',marginTop:2 }}>{a.time}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="card" style={{ padding:0 }}>
        <div className="card-header" style={{ padding:'16px 20px',borderBottom:'1px solid var(--border)' }}>
          <span className="card-title">Rep activity breakdown</span>
          <span style={{ fontSize:12,color:'var(--text-dim)' }}>{period}</span>
        </div>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Rep</th><th>Calls</th><th>Emails sent</th><th>Tasks done</th><th>Deals advanced</th><th>Meetings</th><th>Notes</th><th>Total actions</th></tr></thead>
            <tbody>
              {TODAY_ACTIVITY.map(r=>{
                const total = r.calls+r.emailsSent+r.tasksCompleted+r.dealsAdvanced+r.meetingsHeld+r.notesLogged;
                return (
                  <tr key={r.rep}>
                    <td style={{ fontWeight:500 }}>{r.rep}</td>
                    <td style={{ color:'#4fc3f7',fontWeight:600 }}>{r.calls}</td>
                    <td style={{ color:'var(--accent)',fontWeight:600 }}>{r.emailsSent}</td>
                    <td style={{ color:'#f5a623',fontWeight:600 }}>{r.tasksCompleted}</td>
                    <td style={{ color:'var(--accent)',fontWeight:600 }}>{r.dealsAdvanced}</td>
                    <td>{r.meetingsHeld}</td>
                    <td>{r.notesLogged}</td>
                    <td>
                      <div style={{ display:'flex',alignItems:'center',gap:8 }}>
                        <div style={{ width:60,height:6,background:'var(--border)',borderRadius:3,overflow:'hidden' }}>
                          <div style={{ width:`${(total/50)*100}%`,height:'100%',background:'var(--accent)',borderRadius:3 }}/>
                        </div>
                        <strong style={{ color:'var(--accent)' }}>{total}</strong>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
